import { sanitizeHtml } from './sanitizeHtml';
/**
 * Download-format registry + generators for tailored HR templates.
 *
 * CRM staff enable formats per template (templatesCollections.tailorFormats);
 * the portal Tailor modal offers one download button per enabled format. All
 * heavy libraries load via dynamic import so they never touch the main bundle.
 *
 * Format notes:
 * - pdf   : html2canvas + jsPDF with clean-row pagination (no mid-line cuts)
 * - docx  : html-docx-js (true .docx via MHT altChunk, embeds data-URI images)
 * - xls   : HTML in an Excel container (application/vnd.ms-excel)
 * - xlsx  : SheetJS parses the rendered tables into a true .xlsx
 * - ppt   : html2canvas screenshot sliced into pptxgenjs slides (.pptx)
 */
import { wrapTailoredHtml, TAILORED_BASE_CSS } from './hrTailorTokens';

export const TAILOR_FORMATS = [
  { key: 'pdf', label: 'PDF', portalLabel: 'PDF', ext: 'pdf' },
  { key: 'docx', label: 'DOC', portalLabel: 'Document', ext: 'docx' },
  { key: 'xls', label: 'XLS', portalLabel: 'XLS', ext: 'xls' },
  { key: 'xlsx', label: 'XLSX', portalLabel: 'XLSX', ext: 'xlsx' },
  { key: 'ppt', label: 'PPTX', portalLabel: 'PPTX', ext: 'pptx' },
];

export const DEFAULT_TAILOR_FORMATS = ['pdf'];

/** Legacy 'doc' entries (from before DOC/DOCX merged) count as 'docx'. */
export const normalizeFormatKeys = (list = []) => {
  const normalized = list.map((k) => (k === 'doc' ? 'docx' : k));
  return [...new Set(normalized)];
};

/** Enabled format descriptors for a template doc, in canonical order. */
export const getEnabledFormats = (templateDoc) => {
  const list =
    Array.isArray(templateDoc?.tailorFormats) && templateDoc.tailorFormats.length > 0
      ? normalizeFormatKeys(templateDoc.tailorFormats)
      : DEFAULT_TAILOR_FORMATS;
  return TAILOR_FORMATS.filter((f) => list.includes(f.key));
};

export const sanitizeFileName = (name) => String(name).replace(/[\\/:*?"<>|]+/g, '').trim();

/**
 * Fetch an image and return it as a data URL. Embedding images this way keeps
 * them out of CORS trouble in html2canvas captures and makes them render
 * reliably in Word (html-docx-js doesn't fetch external URLs). Returns null on
 * any failure — callers keep the original URL as a fallback.
 */
export const imageUrlToDataUrl = async (url) => {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!blob.type.startsWith('image/')) return null;
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    return null;
  }
};

const saveBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Offscreen live DOM node styled like the preview — html2canvas needs one.
 * The offscreen positioning lives on a WRAPPER, not the content node: capture
 * clones the target element inline-styles-and-all, so positioning the content
 * itself at left:-10000px produces blank pages.
 * Returns { wrapper, content } — capture `content`, remove `wrapper`.
 */
const mountOffscreen = (bodyHtml, widthPx, paddingPx = 32) => {
  const wrapper = document.createElement('div');
  Object.assign(wrapper.style, {
    position: 'absolute',
    left: '-10000px',
    top: '0',
    width: `${widthPx}px`,
    background: '#ffffff',
  });
  // Same stylesheet the preview iframe uses — the capture must match it.
  const style = document.createElement('style');
  style.textContent = TAILORED_BASE_CSS;
  const content = document.createElement('div');
  content.className = 'tailored-doc';
  content.innerHTML = sanitizeHtml(bodyHtml);
  Object.assign(content.style, {
    width: '100%',
    padding: `${paddingPx}px`,
    // Extra blank tail so sub-pixel rounding at the capture edge can never
    // shave the last line of real content.
    paddingBottom: `${paddingPx + 16}px`,
    boxSizing: 'border-box',
  });
  wrapper.appendChild(style);
  wrapper.appendChild(content);
  document.body.appendChild(wrapper);

  // Pasted templates are often complete HTML documents whose CSS targets
  // `body` — those rules can't reach a <div> mount, so rescope them to the
  // container. The guard set ({ , } whitespace and combinators) avoids
  // mangling class names like .consent-body.
  content.querySelectorAll('style').forEach((s) => {
    s.textContent = s.textContent.replace(/(^|[\s,{}+~>])(html|body)\b/gi, '$1.tailored-doc');
  });

  // html2canvas bug: text-align:center centers text but NOT images. Convert
  // centered images to block + auto margins, which it renders correctly.
  content.querySelectorAll('img').forEach((img) => {
    const parent = img.parentElement;
    if (parent && getComputedStyle(parent).textAlign === 'center') {
      img.style.display = 'block';
      img.style.marginLeft = 'auto';
      img.style.marginRight = 'auto';
    }
  });

  return { wrapper, content };
};

/**
 * Capture the mounted content to a canvas. html2canvas shifts the capture by
 * the page's scroll offset (its long-standing scroll bug), cutting content off
 * the bottom — so scroll to the top for the capture (invisible behind the
 * modal) and restore afterwards.
 */
const captureCanvas = async (html2canvas, content) => {
  const prevX = window.scrollX;
  const prevY = window.scrollY;
  window.scrollTo(0, 0);
  try {
    return await html2canvas(content, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      scrollX: 0,
      scrollY: 0,
    });
  } finally {
    window.scrollTo(prevX, prevY);
  }
};

/**
 * Find a visually clean cut line at-or-above targetY: a pixel row of uniform
 * color (blank space between lines/blocks). Cutting there instead of at the
 * raw page boundary is what keeps text from being sliced in half.
 */
const findCleanBreakRow = (ctx, width, targetY, maxScanUp) => {
  const scanH = Math.min(maxScanUp, targetY);
  if (scanH <= 0) return targetY;
  const band = ctx.getImageData(0, targetY - scanH, width, scanH).data;
  for (let row = scanH - 1; row >= 0; row--) {
    const base = row * width * 4;
    const r0 = band[base];
    const g0 = band[base + 1];
    const b0 = band[base + 2];
    let clean = true;
    for (let x = 1; x < width; x++) {
      const i = base + x * 4;
      if (
        Math.abs(band[i] - r0) > 8 ||
        Math.abs(band[i + 1] - g0) > 8 ||
        Math.abs(band[i + 2] - b0) > 8
      ) {
        clean = false;
        break;
      }
    }
    if (clean) return targetY - scanH + row;
  }
  return targetY;
};

/** Split a rendered canvas into page slices, breaking on clean rows. */
const computeSlices = (canvas, pageHeightPx) => {
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  const slices = [];
  let y = 0;
  while (y < canvas.height) {
    let end = Math.min(y + pageHeightPx, canvas.height);
    if (end < canvas.height) {
      const cleanEnd = findCleanBreakRow(ctx, canvas.width, end, Math.floor(pageHeightPx * 0.3));
      // Only accept the clean break if it still fills most of the page.
      if (cleanEnd > y + pageHeightPx * 0.5) end = cleanEnd;
    }
    slices.push({ y, h: end - y });
    y = end;
  }
  return slices;
};

const sliceToJpeg = (canvas, s) => {
  const slice = document.createElement('canvas');
  slice.width = canvas.width;
  slice.height = s.h;
  const ctx = slice.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, slice.width, slice.height);
  ctx.drawImage(canvas, 0, s.y, canvas.width, s.h, 0, 0, canvas.width, s.h);
  return slice.toDataURL('image/jpeg', 0.95);
};

const downloadPdf = async (bodyHtml, baseName) => {
  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
    import('html2canvas'),
    import('jspdf'),
  ]);
  // 720px = 7.5in printable width (letter minus 0.5in margins) at 96dpi.
  const { wrapper, content } = mountOffscreen(bodyHtml, 720, 0);
  try {
    const canvas = await captureCanvas(html2canvas, content);
    const pageW = 7.5;
    const pageH = 10; // 11in letter minus 0.5in top/bottom margins
    const pxPerIn = canvas.width / pageW;
    const slices = computeSlices(canvas, Math.floor(pageH * pxPerIn));

    const pdf = new jsPDF({ unit: 'in', format: 'letter', orientation: 'portrait' });
    slices.forEach((s, i) => {
      if (i > 0) pdf.addPage();
      pdf.addImage(sliceToJpeg(canvas, s), 'JPEG', 0.5, 0.5, pageW, s.h / pxPerIn);
    });
    pdf.save(`${baseName}.pdf`);
  } finally {
    wrapper.remove();
  }
};

const downloadDocx = async (bodyHtml, baseName) => {
  const mod = await import('html-docx-js/dist/html-docx');
  const htmlDocx = mod.default || mod;
  const blob = htmlDocx.asBlob(wrapTailoredHtml(bodyHtml), {
    margins: { top: 720, right: 720, bottom: 720, left: 720 }, // 0.5in in twips
  });
  saveBlob(blob, `${baseName}.docx`);
};

const downloadXls = (bodyHtml, baseName) => {
  saveBlob(
    new Blob([wrapTailoredHtml(bodyHtml)], { type: 'application/vnd.ms-excel' }),
    `${baseName}.xls`
  );
};

const downloadXlsx = async (bodyHtml, baseName) => {
  const mod = await import('xlsx');
  const XLSX = mod.default || mod;
  let workbook;
  try {
    workbook = XLSX.read(wrapTailoredHtml(bodyHtml), { type: 'string' });
  } catch (e) {
    workbook = null;
  }
  if (!workbook || workbook.SheetNames.length === 0) {
    // No tables in the template — fall back to one text line per block element.
    const el = document.createElement('div');
    el.innerHTML = sanitizeHtml(bodyHtml);
    const rows = Array.from(el.querySelectorAll('h1,h2,h3,h4,p,li,div'))
      .map((n) => (n.children.length === 0 ? n.textContent.trim() : ''))
      .filter(Boolean)
      .map((line) => [line]);
    workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(rows.length ? rows : [['']]), 'Document');
  }
  XLSX.writeFile(workbook, `${baseName}.xlsx`);
};

const downloadPpt = async (bodyHtml, baseName) => {
  const [{ default: html2canvas }, { default: PptxGen }] = await Promise.all([
    import('html2canvas'),
    import('pptxgenjs'),
  ]);
  // 4:3 slide = 10in x 7.5in; render at 96dpi so 960px wide, 720px per slide.
  const { wrapper, content } = mountOffscreen(bodyHtml, 960);
  try {
    const canvas = await captureCanvas(html2canvas, content);
    const pptx = new PptxGen();
    pptx.defineLayout({ name: 'LETTER_4x3', width: 10, height: 7.5 });
    pptx.layout = 'LETTER_4x3';

    // Slide is 10in x 7.5in; break on clean rows so text isn't sliced.
    const slices = computeSlices(canvas, Math.floor(canvas.width * (7.5 / 10)));
    slices.forEach((s) => {
      const slide = pptx.addSlide();
      slide.addImage({
        data: sliceToJpeg(canvas, s),
        x: 0,
        y: 0,
        w: 10,
        h: (s.h / canvas.width) * 10,
      });
    });
    await pptx.writeFile({ fileName: `${baseName}.pptx` });
  } finally {
    wrapper.remove();
  }
};

/**
 * Generate + download the tailored document in the requested format.
 * @param {string} formatKey - one of TAILOR_FORMATS keys
 * @param {{ html: string, fileName: string }} opts - html = token-substituted body HTML; fileName = base name without extension
 */
export const downloadTailored = async (formatKey, { html, fileName }) => {
  const baseName = sanitizeFileName(fileName) || 'document';
  switch (formatKey) {
    case 'pdf':
      return downloadPdf(html, baseName);
    case 'doc': // legacy key, pre-merge
    case 'docx':
      return downloadDocx(html, baseName);
    case 'xls':
      return downloadXls(html, baseName);
    case 'xlsx':
      return downloadXlsx(html, baseName);
    case 'ppt':
      return downloadPpt(html, baseName);
    default:
      throw new Error(`Unknown download format: ${formatKey}`);
  }
};
